import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

@Component({
  selector: 'agm-circle',
  host: { '[attr.data-unique-id]': 'uniqueId' },
  template: `
    <ng-template #infoWindowTemplate>
      <div class="agm-info-window-content">
        <ng-content></ng-content>
      </div>
    </ng-template>
  `,
  standalone: false,
})
export class AgmCircleComponent {
  uniqueId = `obj-${Math.random().toString(36).substr(2, 9)}`; // Genera un ID unico
  // Input per configurare il cerchio
  @Input() latitude!: number;
  @Input() longitude!: number;
  @Input() data: any;
  @Input() radius: number = 1000;
  @Input() fillColor: string = '#FF0000';
  @Input() fillOpacity: number = 0.4;
  @Input() strokeColor: string = '#0000FF';
  @Input() strokeOpacity: number = 0.8;
  @Input() strokeWeight: number = 2;
  @Input() strokePosition;
  @Input() circleDraggable: boolean;
  @Input() editable: boolean;
  @Input() visible: boolean;

  // Opzioni avanzate tramite oggetto `options`
  @Input() options: any = null;
  @Input() marker: any = null; // Marker associato al cerchio (opzionale)

  // Output per gli eventi
  @Output() circleClick = new EventEmitter<google.maps.MapMouseEvent>();
  @Output() radiusChange = new EventEmitter<number>();
  @Output() centerChange = new EventEmitter<google.maps.LatLngLiteral>();
  @Output() dragEnd = new EventEmitter<google.maps.LatLngLiteral>();
  @Input() clickable: boolean = false;
  @Input() enableInfoWindow: boolean = false; // Default: info window abilitata

  private circle: google.maps.Circle | null = null;
  private infoWindow!: google.maps.InfoWindow;

  @ViewChild('infoWindowTemplate', { static: true }) infoWindowTemplate!: TemplateRef<any>;
  @ViewChild('infoWindowTemplate', { static: true, read: ViewContainerRef })
  viewContainerRef!: ViewContainerRef;

  // questo getter per esporre `circle`
  get googleCircle(): google.maps.Circle | null {
    return this.circle;
  }

  initialize(map: google.maps.Map) {
    // Combina gli input individuali con le opzioni
    const circleOptions: google.maps.CircleOptions = {
      center: new google.maps.LatLng({ lat: this.latitude, lng: this.longitude }),
      radius: this.radius,
      fillColor: this.options?.fillColor || this.fillColor,
      fillOpacity: this.options?.fillOpacity ?? this.fillOpacity,
      strokeColor: this.options?.strokeColor || this.strokeColor,
      strokeOpacity: this.options?.strokeOpacity ?? this.strokeOpacity,
      strokeWeight: this.options?.strokeWeight || this.strokeWeight,
      strokePosition: this.options?.strokePosition || this.strokePosition,
      draggable: this.options?.draggable ?? this.circleDraggable,
      editable: this.options?.editable ?? this.editable,
      visible: this.options?.visible ?? this.visible,
      map,
    };

    this.circle = new google.maps.Circle(circleOptions);

    // Configurazione dell'info window
    this.infoWindow = new google.maps.InfoWindow({
      content: this.renderDynamicContent(),
    });

    // Listener per il click sul cerchio
    if (circleOptions.visible && this.clickable) {
      this.circle.addListener('click', (event: google.maps.MapMouseEvent) => {
        this.circleClick.emit(event);
        if (this.enableInfoWindow) {
          this.infoWindow.setPosition(event.latLng); // Posiziona l'info window
          this.infoWindow.open(map); // Apre l'info window
        }
      });
    }

    // Listener per il cambio di raggio
    this.circle.addListener('radius_changed', () => {
      const radius = this.circle ? this.circle.getRadius() : null;
      if (radius !== null) {
        this.radiusChange.emit(radius);
      }
    });

    // Listener per il cambio del centro
    this.circle.addListener('center_changed', () => {
      const center = this.circle ? this.circle.getCenter() : null;
      if (center) {
        this.centerChange.emit({ lat: center.lat(), lng: center.lng() });
      }
    });

    // Listener per il drag end
    if (circleOptions.draggable) {
      this.circle.addListener('dragend', () => {
        const position = this.circle ? this.circle.getCenter() : null;
        if (position) {
          this.dragEnd.emit({ lat: position.lat(), lng: position.lng() });
        }
      });
    }

    // Aggiungi il marker, se specificato
    if (this.marker) {
      this.addMarker(map);
    }
  }

  /**
   * Aggiunge un marker al centro del cerchio, se specificato nei dati.
   */
  private addMarker(map: google.maps.Map): void {
    const markerOptions: google.maps.MarkerOptions = {
      position: { lat: this.marker.lat, lng: this.marker.lng },
      map,
      draggable: this.marker.draggable || false,
      icon: this.marker.icon && this.parseIcon(this.marker.icon),
      label: this.marker.label || undefined,
    };

    new google.maps.Marker(markerOptions);
  }

  /**
   * Parsea l'icona del marker dal formato del mock.
   */
  private parseIcon(icon: any): google.maps.Icon {
    return {
      url: icon.url,
      anchor: icon.anchor ? new google.maps.Point(icon.anchor.x, icon.anchor.y) : undefined,
      origin: icon.origin ? new google.maps.Point(icon.origin.x, icon.origin.y) : undefined,
    };
  }

  private renderDynamicContent(): HTMLElement {
    const element = document.createElement('div');
    const embeddedView = this.viewContainerRef.createEmbeddedView(this.infoWindowTemplate);

    // Rendi il contenuto visibile
    embeddedView.detectChanges();

    // Aggiungi il contenuto generato dinamicamente
    embeddedView.rootNodes.forEach((node) => element.appendChild(node));
    return element;
  }

  getCenter(): google.maps.LatLngLiteral {
    return { lat: this.latitude, lng: this.longitude };
  }

  getRadius(): number {
    return this.radius;
  }

  openInfoWindow(): void {
    if (this.circle) {
      this.infoWindow.open(this.circle.getMap(), this.circle);
    }
  }

  closeInfoWindow(): void {
    this.infoWindow.close();
  }

  remove(): void {
    if (this.circle) {
      this.circle.setMap(null);
      this.circle = null;
    }
  }
}
